import React, { useState, useEffect } from 'react';
import {
  Modal, Table, Button, Space, Form, Input, InputNumber,
  message, Popconfirm, Tag, Switch, Tooltip
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  SaveOutlined, CloseOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import dnsService from '../../services/dnsService';

const QosManageModal = ({ visible, onCancel, zoneId, zoneName }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [form] = Form.useForm();

  // 加载 QoS 策略
  const loadPolicies = async () => {
    if (!zoneId) return;
    setLoading(true);
    try {
      const result = await dnsService.getQosPolicies(zoneId);
      if (result.success) {
        setPolicies(result.data || []);
      } else {
        message.error(result.error || '获取QoS策略失败');
      }
    } catch (error) {
      message.error('获取QoS策略失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && zoneId) {
      loadPolicies();
    }
  }, [visible, zoneId]);

  const isEditing = (record) => record.id === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      name: record.name,
      rate_limit: record.rate_limit,
      burst: record.burst,
      status: record.status === 'active',
    });
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey('');
    // 如果是新记录，删除它
    setPolicies(policies.filter(p => p.id !== 'new'));
  };

  const save = async (id) => {
    try {
      const row = await form.validateFields();
      const policyData = {
        ...row,
        status: row.status ? 'active' : 'inactive',
      };

      if (id === 'new') {
        // 创建新策略
        policyData.zone_id = zoneId;
        const result = await dnsService.createQosPolicy(policyData);
        if (result.success) {
          message.success('QoS策略创建成功');
          setEditingKey('');
          loadPolicies();
        } else {
          message.error(result.error || '创建失败');
        }
      } else {
        // 更新现有策略
        policyData.id = id;
        const result = await dnsService.updateQosPolicy(policyData);
        if (result.success) {
          message.success('QoS策略更新成功');
          setEditingKey('');
          loadPolicies();
        } else {
          message.error(result.error || '更新失败');
        }
      }
    } catch (error) {
      message.error('保存失败: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await dnsService.deleteQosPolicy(id);
      if (result.success) {
        message.success('QoS策略删除成功');
        loadPolicies();
      } else {
        message.error(result.error || '删除失败');
      }
    } catch (error) {
      message.error('删除失败: ' + error.message);
    }
  };

  const addPolicy = () => {
    if (editingKey !== '') {
      message.warning('请先保存或取消当前编辑');
      return;
    }
    const newPolicy = {
      id: 'new',
      name: 'default',
      rate_limit: 100,
      burst: 200,
      status: 'active',
    };
    setPolicies([newPolicy, ...policies]);
    form.setFieldsValue({
      name: 'default',
      rate_limit: 100,
      burst: 200,
      status: true,
    });
    setEditingKey('new');
  };

  const columns = [
    {
      title: '策略名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Form.Item
              name="name"
              style={{ margin: 0 }}
              rules={[{ required: true, message: '请输入策略名称' }]}
            >
              <Input placeholder="如: default, premium" />
            </Form.Item>
          );
        }
        return <Tag color="blue">{text}</Tag>;
      },
    },
    {
      title: '速率限制 (请求/秒)',
      dataIndex: 'rate_limit',
      key: 'rate_limit',
      width: 180,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Form.Item
              name="rate_limit"
              style={{ margin: 0 }}
              rules={[{ required: true, message: '请输入速率限制' }]}
            >
              <InputNumber min={1} max={10000000} style={{ width: '100%' }} />
            </Form.Item>
          );
        }
        return (
          <Tooltip title="每秒允许的最大请求数">
            <span>{text?.toLocaleString()} req/s</span>
          </Tooltip>
        );
      },
    },
    {
      title: '突发限制',
      dataIndex: 'burst',
      key: 'burst',
      width: 150,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Form.Item
              name="burst"
              style={{ margin: 0 }}
              rules={[{ required: true, message: '请输入突发限制' }]}
            >
              <InputNumber min={1} max={10000000} style={{ width: '100%' }} />
            </Form.Item>
          );
        }
        return (
          <Tooltip title="令牌桶突发容量">
            <span>{text?.toLocaleString()}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Form.Item
              name="status"
              style={{ margin: 0 }}
              valuePropName="checked"
            >
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          );
        }
        return (
          <Tag color={text === 'active' ? 'green' : 'default'}>
            {text === 'active' ? '启用' : '禁用'}
          </Tag>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button
              type="link"
              icon={<SaveOutlined />}
              onClick={() => save(record.id)}
              size="small"
            >
              保存
            </Button>
            <Button
              type="link"
              icon={<CloseOutlined />}
              onClick={cancel}
              size="small"
            >
              取消
            </Button>
          </Space>
        ) : (
          <Space>
            <Button
              type="link"
              icon={<EditOutlined />}
              disabled={editingKey !== ''}
              onClick={() => edit(record)}
              size="small"
            >
              编辑
            </Button>
            <Popconfirm
              title="确定删除此QoS策略吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                disabled={editingKey !== ''}
                size="small"
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <ThunderboltOutlined />
          <span>QoS 流量控制设置 - {zoneName}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="close" onClick={onCancel}>
          关闭
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 4,
            padding: '8px 12px',
            marginBottom: 8
          }}>
            <strong>QoS 策略说明：</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
              <li><strong>速率限制</strong>：每秒允许的最大 DNS 查询请求数</li>
              <li><strong>突发限制</strong>：令牌桶容量，允许短时间内的突发请求</li>
              <li>超过限制的请求将返回 REFUSED 响应</li>
            </ul>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={addPolicy}
            disabled={editingKey !== ''}
          >
            添加 QoS 策略
          </Button>
        </Space>
      </div>

      <Form form={form} component={false}>
        <Table
          columns={columns}
          dataSource={policies}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="middle"
          locale={{ emptyText: '暂无 QoS 策略，点击上方按钮添加' }}
        />
      </Form>
    </Modal>
  );
};

export default QosManageModal;
